import { useMantineTheme, createStyles, Group, Text, Anchor, ThemeIcon } from '@mantine/core';
import { IconBrandTwitter, IconBrandLinkedin } from '@tabler/icons-react';
import { useMediaQuery } from "@mantine/hooks";

const useStyles = createStyles((theme) => ({

    footer: {
        paddingTop: '25px',
        paddingBottom: '25px',
        background: theme.colors.dark[9],
        color: theme.colors.gray[4],
        [theme.fn.smallerThan('xs')]: {
            paddingTop: theme.spacing.md,
            paddingBottom: theme.spacing.md,
        },
    },

    inner: {
        width: '100%',
        maxWidth: '1100px',
        padding: '0px 25px',
        margin: '0 auto',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: theme.spacing.sm,
        paddingBottom: theme.spacing.sm,
        [theme.fn.smallerThan('xxl')]: {
            maxWidth: '1050px',
        },
        [theme.fn.smallerThan('sm')]: {
            flexDirection: 'column',
            textAlign: 'center',
            padding: '0px 25px',
        },
    },

    linkGroup: {
        [theme.fn.smallerThan('xs')]: {
            justifyContent: 'center'
        },
    },

    linkText: {
        color: theme.colors.gray[4],
        '&:hover': {
            color: 'white',
        },
    },

    bullets: {
        fontSize: '8px',
        // [theme.fn.smallerThan('sm')]: { display: 'none' },
    },

    iconsGroup: {
        padding: '0px',
        marginTop: theme.spacing.md,
        [theme.fn.smallerThan('sm')]: {
            marginTop: theme.spacing.lg,
        },
    },

    linkIconContainer: {
        display: 'block',
        background: 'none',
    },

    linkIcon: {
        stroke: theme.colors.gray[4],
        '&:hover': {
            stroke: 'white',
        },
    },

    copyrightContainer: {
        width: '100%',
        padding: '0px',
        marginTop: theme.spacing.md,
        [theme.fn.smallerThan('sm')]: {
            marginTop: theme.spacing.lg,
        },
    },

    smallScreenLinkGroup: {
        justifyContent: 'center',
        width: '100%',
    }

}));

const links = [
    {
        label: 'Dashboard',
        link: 'https://app.roundtable.ai/dashboard',
        type: 'external',
        group: 1,
    },
    {
        label: 'Docs',
        link: 'https://alias-docs.roundtable.ai',
        type: 'external',
        group: 1,
    },
    {
        label: 'Blog',
        link: 'https://blog.roundtable.ai',
        type: 'external',
        group: 1,
    },
    {
        label: 'Contact',
        link: 'mailto:support@roundtable.ai',
        type: 'email',
        group: 2,
    },
    {
        label: 'Terms',
        link: 'https://app.roundtable.ai/terms',
        type: 'internal',
        group: 2,
    },
    {
        label: 'Privacy',
        link: 'https://app.roundtable.ai/privacy',
        type: 'internal',
        group: 2,
    },
];

const currentYear = new Date().getFullYear();

const AppFooter = () => {

    const { classes } = useStyles();
    const theme = useMantineTheme();
    const isSmall = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`);

    const uniqueGroups = [...new Set(links.map((link) => link.group))];

    return (
        <div className={classes.footer}>
            <div className={classes.inner} >
                <Group className={classes.linkGroup}>
                    {
                        isSmall ?
                            // Loop through unique groups of links
                            uniqueGroups.map((group) => (
                                <Group
                                    key={group}
                                    spacing='xs'
                                    className = {classes.smallScreenLinkGroup}
                                >
                                    {
                                        // Loop through links
                                        links.map((link, i) => (
                                            // If link is in group, show link
                                            link.group === group &&
                                            <Group
                                                key={link.label} className={classes.linkContainer}
                                            >
                                                <Anchor target='_blank' href={link.link}>
                                                    <Text className={classes.linkText}>{link.label}</Text>
                                                </Anchor>
                                                {
                                                    // Check if link is last in group
                                                    (i !== links.length - 1 && links[i+1].group === group) && 
                                                    <Text className = {classes.bullets}>•</Text>
                                                }
                                            </Group>
                                        ))
                                    }
                                </Group>
                            ))
                            :
                            // Loop through links
                            links.map((link, i) => (
                                <Group
                                    key={link.label} className={classes.linkContainer}
                                    spacing='sm'
                                >
                                    <Anchor target='_blank' href={link.link}>
                                        <Text className={classes.linkText}>{link.label}</Text>
                                    </Anchor>
                                    { i !== links.length - 1 && 
                                    <Text className = {classes.bullets}>•</Text>
                                    }
                                </Group>
                            ))
                    }
                </Group>
                <Group className={classes.iconsGroup} spacing='xl'>
                    <Anchor target='_blank' href='https://twitter.com/RoundtableDotAI'>
                        <ThemeIcon className={classes.linkIconContainer}>
                            <IconBrandTwitter
                                stroke={1.5}
                                className={classes.linkIcon}
                            />
                        </ThemeIcon>
                    </Anchor>
                    <Anchor target='_blank' href='https://www.linkedin.com/company/91449803'>
                        <ThemeIcon className={classes.linkIconContainer}>
                            <IconBrandLinkedin
                                stroke={1.5}
                                className={classes.linkIcon}
                            />
                        </ThemeIcon>
                    </Anchor>
                </Group>
                <div className={classes.copyrightContainer}>
                    <Text>Copyright © {currentYear} Roundtable Technologies, Inc. All rights reserved.</Text>
                </div>
            </div>
        </div>
    );
}

export default AppFooter;